import {
  Button,
  Group,
  Modal,
  Rating,
  Stack,
  Text,
  Textarea,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useState } from "react";
import { client } from "@/api-client";

interface ReviewModalProps {
  opened: boolean;
  onClose: () => void;
  trainerId: string;
  trainerFirstName: string;
  onSuccess?: () => void;
}

export function ReviewModal({
  opened,
  onClose,
  trainerId,
  trainerFirstName,
  onSuccess,
}: ReviewModalProps) {
  const [submitted, setSubmitted] = useState(false);

  const form = useForm({
    initialValues: {
      rating: 0,
      comment: "",
    },
    validate: {
      rating: (value) => (value === 0 ? "Please select a rating" : null),
    },
  });

  const submitMutation = client.useMutation(
    "post",
    "/owner/trainers/{trainerId}/reviews",
  );

  const onSubmit = async (values: { rating: number; comment: string }) => {
    await submitMutation.mutateAsync({
      params: { path: { trainerId } },
      body: {
        rating: values.rating,
        comment: values.comment || null,
      },
    });
    setSubmitted(true);
    onSuccess?.();
  };

  const handleClose = () => {
    form.reset();
    setSubmitted(false);
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={submitted ? "Thank you!" : `Review ${trainerFirstName}`}
    >
      {submitted ? (
        <Stack>
          <Text>
            Your review has been submitted. It will help other dog owners find
            the right trainer.
          </Text>
          <Button onClick={handleClose}>Close</Button>
        </Stack>
      ) : (
        <form onSubmit={form.onSubmit(onSubmit)}>
          <Stack>
            <Stack gap="xs">
              <Text size="sm" fw={500}>
                Your rating
              </Text>
              <Rating
                size="xl"
                value={form.values.rating}
                onChange={(value) => form.setFieldValue("rating", value)}
              />
              {form.errors.rating && (
                <Text size="xs" c="red">
                  {form.errors.rating}
                </Text>
              )}
            </Stack>

            <Textarea
              label="Your experience (optional)"
              placeholder={`Tell other dog owners about your experience with ${trainerFirstName}...`}
              autosize
              minRows={3}
              maxLength={1000}
              {...form.getInputProps("comment")}
            />

            <Group justify="flex-end">
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                loading={submitMutation.isPending}
                disabled={form.values.rating === 0}
              >
                Submit Review
              </Button>
            </Group>
          </Stack>
        </form>
      )}
    </Modal>
  );
}
